<script>
import PainelCardVivo from '@modules/aawz-intelligence-v2/components/painel-card-vivo/painel-card-vivo.vue'
import PainelDivergencias from '@modules/aawz-intelligence-v2/components/painel-divergencias/painel-divergencias.vue'
import PainelTurnoDetalhes from '@modules/aawz-intelligence-v2/components/painel-turno-detalhes/painel-turno-detalhes.vue'
import PainelOrquestrador from '@modules/aawz-intelligence-v2/components/painel-orquestrador/painel-orquestrador.vue'
import { CLASSE_SHIMMER_TEXTO } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { ESTADO_CONCLUIDO, agruparPorRodada } from '@modules/aawz-intelligence-v2/utils/painel-deliberacao'
import {
  marcadorDaRodada,
  resumoDaRodada,
  rotuloDoBotaoDaRodada,
} from '@modules/aawz-intelligence-v2/utils/painel-deliberacao-rodadas'
import {
  ROTULO_CONDUZ,
  ROTULO_EXPANDIR,
  ROTULO_RECOLHER,
  SOBRETITULO_DA_SALA,
  TEXTO_NAO_RESPONDEU,
  TITULO_DAS_RODADAS,
  TITULO_DAS_RODADAS_SEM_PARECER,
  TITULO_DA_SALA,
  condutorDaMesa,
  decorarDivergencias,
  decorarOrquestrador,
  decorarTurno,
  metricaDoPainel,
  montarCardsVivos,
  rotulosDaMesa,
  textoDoEncerramento,
  tituloDaRodada,
} from '@modules/aawz-intelligence-v2/utils/painel-deliberacao-view'

const CLASSE_SETA_ABERTA = 'ia-block-seta--aberta'

export default {
  name: 'IntelligenceV2PainelDeliberacao',
  components: { PainelCardVivo, PainelDivergencias, PainelOrquestrador, PainelTurnoDetalhes },
  props: {
    // Aceita os dois shapes: a transcrição completa (mensagem persistida) e o
    // parcial de `reduzirEventos` (`aoVivo: true`). A decoração é a mesma — é o
    // que faz a sala ao vivo e a reaberta serem idênticas.
    painel: {
      type: Object,
      default: null,
    },
    // Durante o streaming o painel já terminou, mas a resposta ainda está sendo
    // escrita — vale sinalizar que o Consultor está trabalhando sobre isto.
    emAndamento: {
      type: Boolean,
      default: false,
    },
    // Turno acabou: a mesa vira andaime e sai da frente. (A `raciocinio-linha`
    // um nível acima nasce recolhida e só abre pelo clique.)
    // Clique manual continua vencendo em qualquer momento.
    recolher: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // `null` = ninguém clicou; a abertura segue o `recolher`.
      aberturaManual: null,
      // Abertura POR RODADA, só quando alguém clicou (chave ausente = o
      // default de `rodadasRecolhidas`). Objeto trocado inteiro a cada clique:
      // em Vue 2, chave acrescentada depois em objeto existente não é reativa.
      aberturaManualPorRodada: {},
      // Expansão por turno. Reconstruído inteiro a cada mudança de payload (ver
      // watcher): em Vue 2, chave acrescentada depois em objeto existente não é
      // reativa, e o `$set` tardio é justamente o que se evita aqui.
      expandidos: {},
      // O custo da inferência é informação de operação da AAWZ, não do gestor
      // do escritório: para ele é um número sem contexto ao lado de uma
      // resposta que ele não pediu para precificar. Mesmo gate do resto do repo
      // (`$currentUser().IS_INTERNAL`, ver `salvar-empresa.vue`).
      //
      // `data`, e não `computed`: o usuário não muda no meio da sessão, então
      // ler uma vez basta — e, como `$currentUser` é método do protótipo (não
      // uma dependência reativa), um computed ficaria cacheado para sempre e o
      // teste não teria como sobrescrever.
      ehInterno: Boolean(this.$currentUser().IS_INTERNAL),
    }
  },
  computed: {
    // Mesmo shimmer dos blocos irmãos (raciocínio, próximos passos): pulso de
    // opacidade, não o gradiente recortado do protótipo.
    classeShimmer() {
      return CLASSE_SHIMMER_TEXTO
    },
    sobretitulo() {
      return SOBRETITULO_DA_SALA
    },
    titulo() {
      return TITULO_DA_SALA
    },
    temPainel() {
      const p = this.painel
      if (!p) return false
      if (p.turnos && p.turnos.length) return true
      // Mesa montada e ninguém falou ainda: a sala existe e mostra os cards.
      // Um parecer consolidado sozinho também basta — sem isso, uma deliberação
      // que só produziu o fechamento sumiria da tela.
      return Boolean(this.cardsVivos.length || this.orquestrador)
    },
    modo() {
      return (this.painel && this.painel.modo) || ''
    },
    // A mesa é a fonte ÚNICA do nome de negócio de cada área — o turno, as
    // divergências e o parecer leem daqui. Ver `identidadeDoTurno`.
    rotulosDasAreas() {
      return rotulosDaMesa(this.painel && this.painel.mesa)
    },
    // Os grupos crus: rodada + turnos decorados, na ordem que o BACKEND fixou.
    // Aqui só se recorta; reordenar faria a sala ao vivo e a reaberta mostrarem
    // o debate em ordens diferentes.
    //
    // A rodada 1 é criada mesmo sem turno algum quando há cards ao vivo: entre a
    // abertura e o primeiro parecer, é ela que dá casa aos cards da mesa.
    gruposDeTurnos() {
      const p = this.painel
      const modo = this.modo
      const rotulos = this.rotulosDasAreas
      const grupos = !p || !p.turnos || !p.turnos.length
        ? []
        : agruparPorRodada(p).map(function(grupo) {
          return {
            rodada: grupo.rodada,
            turnos: grupo.turnos.map(function(turno, indice) {
              return decorarTurno(turno, indice, modo, rotulos)
            }),
          }
        })
      const comTurno = {}
      for (const grupo of grupos) comTurno[grupo.rodada] = true
      // Rodada que só tem card (a mesa recém-aberta, ou uma réplica sendo
      // escrita) precisa do próprio divisor — senão a fala apareceria solta.
      const soComCard = []
      for (const card of this.cardsVivos) {
        if (comTurno[card.rodada] || soComCard.includes(card.rodada)) continue
        soComCard.push(card.rodada)
      }
      if (!soComCard.length) return grupos
      const novos = soComCard.map(function(rodada) {
        return { rodada: rodada, turnos: [] }
      })
      return grupos.concat(novos).sort(function(a, b) { return a.rodada - b.rodada })
    },
    // A deliberação recolhe quando há CONCLUSÃO acima dela para ler [owner,
    // 19/09/2026]: *"Parecer primeiro, rodadas recolhidas embaixo"*. Sem
    // parecer, a deliberação É o conteúdo — escondê-la deixaria o cartão vazio,
    // e é o que mantém a conversa anterior à W7 exatamente como ela sempre foi.
    temParecerConsolidado() {
      return Boolean(this.orquestrador)
    },
    // Ao vivo a sala é para ASSISTIR: recolher as rodadas esconderia justamente
    // as áreas escrevendo. Quando o painel final substitui o parcial, ela
    // recolhe sozinha — a mesma regra do `recolher` do cartão inteiro.
    rodadasRecolhidas() {
      if (this.painel && this.painel.aoVivo) return false
      return this.temParecerConsolidado
    },
    tituloDasRodadas() {
      return this.temParecerConsolidado ? TITULO_DAS_RODADAS : TITULO_DAS_RODADAS_SEM_PARECER
    },
    // Rodadas já decoradas: o template não chama função — cada re-render
    // reexecutaria a decoração inteira.
    rodadas() {
      const modo = this.modo
      const cards = this.cardsPorRodada
      const manual = this.aberturaManualPorRodada
      const padraoAberta = !this.rodadasRecolhidas
      return this.gruposDeTurnos.map(function(grupo) {
        const titulo = tituloDaRodada(grupo.rodada, modo)
        // `Object.hasOwn`, nunca `in`: o mapa é objeto literal e herda de
        // `Object.prototype` — mesma guarda de `valorDoMapa`.
        const aberta = Object.hasOwn(manual, grupo.rodada) ? manual[grupo.rodada] : padraoAberta
        return {
          ...grupo,
          titulo: titulo,
          marcador: marcadorDaRodada(grupo.rodada),
          resumo: resumoDaRodada(grupo.turnos, cards[grupo.rodada]),
          aberta: aberta,
          rotulo: rotuloDoBotaoDaRodada(titulo, aberta),
          classeSeta: aberta ? CLASSE_SETA_ABERTA : '',
        }
      })
    },
    // Rótulo do botão de expansão por turno. Computed à parte para que alternar
    // um turno não reexecute a decoração de todos.
    rotulosDeExpansao() {
      const rotulos = {}
      for (const grupo of this.rodadas) {
        for (const turno of grupo.turnos) {
          rotulos[turno.chave] = this.expandidos[turno.chave] ? ROTULO_RECOLHER : ROTULO_EXPANDIR
        }
      }
      return rotulos
    },
    orquestrador() {
      return decorarOrquestrador(this.painel)
    },
    // Tudo o que pode abrir e fechar nesta sala. O watcher reconstrói o mapa de
    // expansão a partir daqui: chave acrescentada depois, em objeto que já
    // existe, não é reativa em Vue 2.
    chavesExpansiveis() {
      const chaves = []
      for (const grupo of this.rodadas) {
        for (const turno of grupo.turnos) {
          if (turno.expansao.temConteudo) chaves.push(turno.chave)
        }
      }
      return chaves
    },
    cardsVivos() {
      return montarCardsVivos(this.painel)
    },
    // Cards agrupados pela rodada em que a fala está acontecendo — o template
    // indexa por rodada em vez de filtrar (sem função no template).
    cardsPorRodada() {
      const porRodada = {}
      for (const card of this.cardsVivos) {
        if (!porRodada[card.rodada]) porRodada[card.rodada] = []
        porRodada[card.rodada].push(card)
      }
      return porRodada
    },
    // Mesmo rótulo do card vivo da área que não respondeu (`montarCardsVivos`):
    // uma fonte só, para que o turno indisponível e o card nunca divirjam.
    textoNaoRespondeu() {
      return TEXTO_NAO_RESPONDEU
    },
    // A palavra é a MESMA do resumo do cabeçalho ("Jurídico e Regulatório
    // conduz") — duas strings soltas divergiriam no primeiro ajuste de copy.
    rotuloConduz() {
      return ROTULO_CONDUZ
    },
    divergencias() {
      const p = this.painel
      if (!p) return []
      return decorarDivergencias(p.parecer, p.mesa)
    },
    // Só o condutor, não a mesa inteira: os nomes das áreas já estão no corpo,
    // um por turno, com a tag "conduz". Repetir a lista no cabeçalho fazia a
    // faixa quebrar em duas linhas com 3 áreas — e a mesa pode ter 7.
    // Sem condutor declarado a métrica fica sozinha — nada de " · " órfão.
    metaDoCabecalho() {
      return [this.mesaResumo, this.metrica].filter(Boolean).join(' · ')
    },
    mesaResumo() {
      return condutorDaMesa(this.painel)
    },
    metrica() {
      return metricaDoPainel(this.painel, { mostrarCusto: this.ehInterno })
    },
    // O custo sai de tokens reportados pelo provider; quando alguma chamada não
    // reporta, o número é estimativa — e dizer isso é mais barato que descobrir
    // depois que o valor exibido não bate com o cobrado.
    //
    // Preso ao `ehInterno` pelo mesmo motivo: o "~" qualifica o custo, e sem o
    // valor ao lado vira um til solto no cabeçalho.
    custoAproximado() {
      if (!this.ehInterno) return false
      return Boolean(this.painel && this.painel.custo_aproximado)
    },
    encerramento() {
      if (!this.painel) return ''
      return textoDoEncerramento(this.painel.encerramento)
    },
    aberto() {
      if (this.aberturaManual !== null) return this.aberturaManual
      return !this.recolher
    },
    classeSeta() {
      return this.aberto ? CLASSE_SETA_ABERTA : ''
    },
    rotuloDoBotao() {
      return this.aberto ? 'Ocultar a deliberação' : 'Ver a deliberação'
    },
    // Sala em atividade: ou a deliberação ainda corre (`aoVivo`), ou o Consultor
    // escreve a resposta a partir dela. O dot é decorativo (aria-hidden) — o
    // estado é anunciado pelas linhas de status que já existem.
    salaAtiva() {
      return Boolean((this.painel && this.painel.aoVivo) || this.emAndamento)
    },
    // "K/N áreas" do desenho do protótipo — só ao vivo, contando o ESTADO ATUAL
    // de cada área (`estadoPorArea` é chave só-do-vivo). Reflete a rodada
    // corrente: área que voltou a falar na rodada 2 sai da contagem até fechar.
    // Na sala reaberta este contador não existe e a métrica completa assume.
    contadorAoVivo() {
      const p = this.painel
      if (!p || !p.aoVivo || !p.mesa || !p.mesa.length) return ''
      const estados = p.estadoPorArea || {}
      let concluidas = 0
      for (const membro of p.mesa) {
        if (estados[membro.area] === ESTADO_CONCLUIDO) concluidas++
      }
      return concluidas + '/' + p.mesa.length + ' áreas'
    },
  },
  watch: {
    // Payload novo (turno seguinte, ou o final substituindo o parcial): objeto
    // novo, com todas as chaves reativas desde o início. O que já estava aberto
    // continua aberto — a sala cresce durante o streaming, e recolher o que o
    // gestor abriu a cada evento seria insuportável.
    chavesExpansiveis: {
      immediate: true,
      handler(chaves) {
        const estado = {}
        for (const chave of chaves) estado[chave] = Boolean(this.expandidos[chave])
        this.expandidos = estado
      },
    },
  },
  methods: {
    alternar() {
      this.aberturaManual = !this.aberto
    },
    // Recebe o grupo já decorado: o estado atual da rodada mora nele, e relê-lo
    // aqui duplicaria a regra de default que o `rodadas` já aplicou.
    alternarRodada(grupo) {
      this.aberturaManualPorRodada = {
        ...this.aberturaManualPorRodada,
        [grupo.rodada]: !grupo.aberta,
      }
    },
    alternarTurno(chave) {
      this.expandidos = { ...this.expandidos, [chave]: !this.expandidos[chave] }
    },
  },
}
</script>

<template>
  <div v-if="temPainel" class="painel-deliberacao ia-block-card">
    <!-- ★ [owner, 19/09/2026] O padrão dos cartões: sobretítulo + título em
         serif (`ia-titulo-de-cartao`), a métrica logo abaixo e a ÚNICA ação —
         o chevron — encostada à direita. `button` em vez de `div` porque o
         cabeçalho é o único jeito de reabrir a mesa recolhida; aqui ele não
         carrega outro botão dentro (a regra do `cartao-cabecalho.vue` vale para
         quem tem CTA no canto), então o elemento nativo é o certo. -->
    <button
      type="button"
      class="ia-block-card__cabecalho ia-block-card__cabecalho--clicavel painel-deliberacao__topo"
      :aria-expanded="String(aberto)"
      :aria-label="rotuloDoBotao"
      @click="alternar"
    >
      <span class="painel-deliberacao__titulos">
        <span class="az-ds-label">{{ sobretitulo }}</span>
        <span class="ia-titulo-de-cartao painel-deliberacao__titulo">
          {{ titulo }}
          <span v-if="salaAtiva" class="iv2-dot-pulsante" aria-hidden="true" />
        </span>
        <span class="painel-deliberacao__meta ia-texto-auxiliar">
          <span
            v-if="contadorAoVivo"
            class="painel-deliberacao__contador"
            :class="classeShimmer"
          >{{ contadorAoVivo }}</span>
          <template v-else>{{ metaDoCabecalho }}</template>
          <!-- O "~" só aparece quando alguma chamada não reportou tokens. O
               tooltip diz o porquê em texto — sinal só-visual não seria lido. -->
          <v-tooltip v-if="custoAproximado" bottom>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                class="painel-deliberacao__aproximado"
                v-on="on"
              >~</span>
            </template>
            <span>Custo aproximado: alguma chamada não reportou o consumo de tokens.</span>
          </v-tooltip>
        </span>
      </span>
      <icon-svg
        name="arrow-down-new-ux"
        :size="14"
        color="var(--neutral-700)"
        class="ia-block-seta"
        :class="classeSeta"
      />
    </button>

    <!-- ⚠️ O filho do `v-if` é OCO: em `border-box`, padding no elemento que o
         `v-expand-transition` anima é um PISO que `height: 0` nunca atravessa,
         e a caixa trava no meio do caminho. O respiro mora no `__corpo-interno`
         (receita completa no `CLAUDE.md` da raiz). -->
    <v-expand-transition>
      <div v-if="aberto" class="painel-deliberacao__corpo">
        <div class="painel-deliberacao__corpo-interno az-gap">
          <!-- ★ O PARECER PRIMEIRO [owner]: quem lê quer a conclusão; a
               deliberação abaixo é a evidência dela. -->
          <PainelOrquestrador v-if="orquestrador" :orquestrador="orquestrador" />

          <PainelDivergencias v-if="divergencias.length" :divergencias="divergencias" />

          <div class="painel-deliberacao__deliberacao az-gap-small">
            <div class="az-ds-label-sentenca">{{ tituloDasRodadas }}</div>

            <div v-if="encerramento" class="az-text-secondary az-size-12 az-muted">
              {{ encerramento }}
            </div>

            <div
              v-for="grupo in rodadas"
              :key="grupo.rodada"
              class="painel-deliberacao__rodada"
            >
              <button
                type="button"
                class="painel-deliberacao__rodada-topo"
                :aria-expanded="String(grupo.aberta)"
                :aria-label="grupo.rotulo"
                @click="alternarRodada(grupo)"
              >
                <span class="painel-deliberacao__marcador ia-texto-selo">{{ grupo.marcador }}</span>
                <span class="az-text-primary az-size-12 painel-deliberacao__rodada-titulo">
                  {{ grupo.titulo }}
                </span>
                <span class="az-text-secondary az-size-12 az-muted painel-deliberacao__resumo">
                  {{ grupo.resumo }}
                </span>
                <icon-svg
                  name="arrow-down-new-ux"
                  :size="12"
                  color="var(--neutral-700)"
                  class="ia-block-seta"
                  :class="grupo.classeSeta"
                />
              </button>

              <v-expand-transition>
                <div v-if="grupo.aberta" class="painel-deliberacao__rodada-corpo">
                  <div class="painel-deliberacao__rodada-conteudo az-gap-small">
                    <div
                      v-for="turno in grupo.turnos"
                      :key="turno.chave"
                      class="painel-deliberacao__turno az-gap-xsmall"
                    >
                      <div
                        class="d-flex align-center flex-wrap az-gap-small painel-deliberacao__cabecalho"
                      >
                        <span
                          class="painel-avatar painel-avatar--pronto flex-shrink-0"
                          aria-hidden="true"
                        >
                          <icon-svg :name="turno.icone" :size="14" color="primary" />
                        </span>
                        <span class="az-text-primary az-size-12 font-medium">{{ turno.label }}</span>
                        <!-- ★ Tags DISCRETAS [owner]: "conduz" é papel (neutro) e
                             a posição da réplica mantém o TOM semântico — quem
                             cede muda o resultado, quem mantém deixa a
                             divergência de pé. O rótulo continua em texto. -->
                        <span v-if="turno.lead" class="painel-tag ia-texto-auxiliar">{{ rotuloConduz }}</span>
                        <span v-if="turno.enderecamento" class="az-text-secondary az-size-12 az-muted">
                          {{ turno.enderecamento }}
                        </span>
                        <span
                          v-if="turno.ehReplica"
                          class="painel-tag ia-texto-auxiliar"
                          :class="turno.classeDaPosicao"
                        >{{ turno.posicao_label }}</span>
                        <BaseStatus
                          v-if="turno.indisponivel"
                          soft
                          preserve-case
                          :content="textoNaoRespondeu"
                          color="warning-new"
                        />
                      </div>

                      <div
                        v-if="turno.motivo_indisponivel"
                        class="az-text-secondary az-size-12 az-muted painel-deliberacao__linha"
                      >
                        {{ turno.motivo_indisponivel }}
                      </div>

                      <!-- O trecho contestado, na voz de quem o escreveu. Sem ele, a
                           réplica obriga o gestor a rolar a tela para trás e adivinhar
                           qual item da outra área está em jogo. -->
                      <div v-if="turno.quote" class="painel-deliberacao__quote az-gap-xsmall">
                        <div class="az-text-secondary az-size-12 az-muted">{{ turno.quote.texto }}</div>
                        <div class="az-text-secondary az-size-12 az-muted">{{ turno.quote.atribuicao }}</div>
                      </div>

                      <!-- A FALA é o que o gestor lê primeiro: o especialista falando em
                           primeira pessoa. Os estruturados viram o detalhe dela (P10). -->
                      <div v-if="turno.temFala" class="az-text-secondary az-size-12 painel-deliberacao__fala">
                        {{ turno.fala }}
                      </div>

                      <PainelTurnoDetalhes v-if="turno.estruturadoInline" :turno="turno" />

                      <!-- Link do DS, não botão: a ação é revelar texto na própria
                           página, não executar nada. A seta acompanha o estado, como nos
                           outros blocos colapsáveis do chat. -->
                      <button
                        v-if="turno.expansao.temConteudo"
                        type="button"
                        class="az-ds-hyperlink painel-deliberacao__ver-analise ia-texto-auxiliar"
                        :aria-expanded="String(Boolean(expandidos[turno.chave]))"
                        :aria-label="rotulosDeExpansao[turno.chave] + ' de ' + turno.label"
                        @click="alternarTurno(turno.chave)"
                      >
                        <span>{{ rotulosDeExpansao[turno.chave] }}</span>
                        <icon-svg
                          name="arrow-down-new-ux"
                          :size="12"
                          color="var(--primary)"
                          class="ia-block-seta"
                          :class="expandidos[turno.chave] ? 'ia-block-seta--aberta' : ''"
                        />
                      </button>

                      <v-expand-transition>
                        <div
                          v-if="expandidos[turno.chave]"
                          class="painel-deliberacao__analise az-gap-xsmall"
                        >
                          <PainelTurnoDetalhes v-if="turno.temFala" :turno="turno" />
                          <div
                            v-for="servico in turno.expansao.servicos"
                            :key="servico.chave"
                            class="az-text-secondary az-size-12"
                          >
                            {{ servico.codigo }} — {{ servico.motivo }}
                          </div>
                          <div
                            v-for="pergunta in turno.expansao.perguntas"
                            :key="pergunta.chave"
                            class="az-text-secondary az-size-12 az-muted"
                          >
                            {{ pergunta.texto }}
                          </div>
                          <div
                            v-if="turno.expansao.confianca"
                            class="az-text-secondary az-size-12 az-muted"
                          >
                            Confiança {{ turno.expansao.confianca }} — {{ turno.expansao.confiancaPorque }}
                          </div>
                        </div>
                      </v-expand-transition>
                    </div>

                    <PainelCardVivo
                      v-for="card in cardsPorRodada[grupo.rodada]"
                      :key="card.chave"
                      :card="card"
                    />
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>

          <div v-if="emAndamento" class="d-flex align-center painel-deliberacao__cabecalho">
            <output
              class="az-text-secondary az-size-12 az-muted"
              :class="classeShimmer"
              aria-live="polite"
            >
              O consultor está escrevendo a resposta a partir daqui.
            </output>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<style lang="sass" scoped>
@import '@modules/aawz-intelligence-v2/design/efeitos.sass'

// ---------------------------------------------------------------------------
// Roupa nossa no conteúdo dele.
//
// Os 5 componentes do painel vieram do #1961 com as utilitárias `az-*`, que
// resolvem para 12px / #8E8E8E / `line-height: 12px`. Dois problemas: #8E8E8E
// sobre branco dá 3.5, abaixo do mínimo de 4.5 da WCAG para texto pequeno; e
// entrelinha 1.0 em prosa de 12px é ilegível — as falas das áreas são texto
// para LER, não rótulo para escanear.
//
// Remapeadas aqui, escopadas ao painel, em vez de trocadas nos templates: são
// ~68 usos em 5 arquivos que acabaram de chegar do `desenvolvimento`, e mexer
// em todos multiplicaria a chance de quebrar algo que os testes dele cobrem por
// comportamento, não por aparência.
// ---------------------------------------------------------------------------
// ★ O seletor precisa da classe do bloco no começo: as utilitárias dele são
// COMPOSTAS (`.az-text-secondary.az-size-12`), o que dá (0,2,0) — o mesmo que
// um `::v-deep` simples —, e no empate a folha global vence. Com
// `.painel-deliberacao` na frente sobe para (0,3,0) e o remap pega.
.painel-deliberacao
  ::v-deep .az-size-12,
  ::v-deep .az-text-secondary,
  ::v-deep .az-muted
    font-size: var(--ia-font-auxiliar)
    line-height: 1.6
    color: var(--neutral-700)

  ::v-deep .az-text-primary
    font-size: var(--ia-font-auxiliar)
    line-height: 1.6
    color: var(--neutral-900)

// ⚠️ A MOLDURA é do `.ia-block-card` (hairline quente, `--radius-lg`, branco,
// `overflow: hidden`) — este bloco NÃO a redeclara. Até 19/09/2026 havia aqui
// uma segunda moldura (borda + raio + `background: white`) mais um override de
// `border-color`: três declarações disputando o mesmo pixel, e a palavra-chave
// `white` no lugar do token. Cartão do thread não pinta sombra — a regra mora
// em `design/_ia-block-card.sass`, uma vez, para todos.
.painel-deliberacao
  margin-top: 12px

// O cabeçalho virou `button`: sem o reset ele traria borda, fundo e fonte
// nativos e destoaria dos outros blocos.
.ia-block-card__cabecalho
  width: 100%
  text-align: left
  font: inherit
  border: 0

// ★ Vocabulário de SEÇÃO (`ui-registry/cartao-cabecalho.vue`): sobretítulo,
// título em serif e métrica EMPILHADOS numa coluna, com a ação à direita. O
// alinhamento vertical do chevron é o `align-items: center` que o
// `.ia-block-card__cabecalho` já dá — com a coluna recolhida ou aberta, ele
// acompanha o centro dela, sem classe que troque de valor no meio da animação.
.painel-deliberacao__topo
  align-items: center
  padding: 14px

.painel-deliberacao__titulos
  display: flex
  flex-direction: column
  // Piso de largura: os irmãos do topo são `flex-shrink-0`, e sem o piso o
  // título serif era espremido no drawer estreito da Jornada.
  flex: 1 1 150px
  min-width: 150px
  gap: 2px

// A serif divide a linha com o dot de "sala em atividade".
.painel-deliberacao__titulo
  display: inline-flex
  align-items: center
  gap: 8px

// ⚠️ Cor declarada aqui, e não herdada: o cabeçalho é um `<button>`, e `color`
// de botão herda do agente do usuário em alguns navegadores — foi assim que a
// linha de métricas de outro cartão nasceu na cor de destaque em vez de muted.
.painel-deliberacao__meta
  color: var(--neutral-700)
  display: inline-flex
  align-items: center
  gap: 4px

.painel-deliberacao__contador
  font-weight: 500

// O sinal de custo aproximado é um ativador de tooltip: o cursor precisa dizer
// que há explicação ali.
.painel-deliberacao__aproximado
  cursor: help

// `contain` isola layout e paint do resto da página: sem isso, o `v-expand-transition`
// (que anima `height` via inline style, fora da GPU) força recálculo da página inteira
// a cada frame e trava visivelmente no fim da animação. `paint` é necessário além de
// `layout` porque os turnos têm fundo e borda próprios.
.painel-deliberacao__corpo
  contain: layout paint
  will-change: height

// O respiro mora AQUI, e não no `__corpo` — ver o aviso no template.
.painel-deliberacao__corpo-interno
  display: flex
  flex-direction: column
  padding: 0 14px 12px

.painel-deliberacao__deliberacao
  display: flex
  flex-direction: column

// ★ A linha RECOLHIDA da rodada: marcador à esquerda, título no meio, resumo
// encostado à direita e o chevron no fim. É um `button` de largura inteira —
// o alvo do clique é a linha, não só a seta.
.painel-deliberacao__rodada
  display: flex
  flex-direction: column
  border-top: 1px solid var(--border-hairline)

.painel-deliberacao__rodada-topo
  display: flex
  align-items: center
  gap: 8px
  width: 100%
  padding: 8px 0
  border: 0
  background: none
  font: inherit
  text-align: left
  cursor: pointer

  // `.ia-block-seta` já traz `margin-left: auto` para encostar no fim da faixa;
  // aqui quem paga o `auto` é o resumo, e a seta anda colada nele.
  .ia-block-seta
    margin-left: 0

// O marcador é a ÂNCORA da pilha de linhas fechadas — o título é uma frase, e
// frase não serve de ponto de fixação para o olho.
.painel-deliberacao__marcador
  color: var(--neutral-700)
  flex-shrink: 0
  min-width: 22px
  text-align: center
  background: var(--neutral-100)
  border-radius: var(--radius-sm)

// Era caixa alta com `letter-spacing` — a linguagem de ETIQUETA. Mas o texto é
// uma frase inteira ("Rodada 1 — cada área lê o caso sozinha"), e frase em caixa
// alta é mais lenta de ler e grita no meio do bloco.
.painel-deliberacao .painel-deliberacao__rodada-titulo
  flex: 1 1 auto
  min-width: 0
  text-transform: none
  letter-spacing: normal
  font-weight: 600

// O resumo é a PROMESSA da linha fechada: ele empurra o chevron para a direita.
.painel-deliberacao__resumo
  flex-shrink: 0
  margin-left: auto

// Mesmas duas camadas do corpo do cartão, pelo mesmo motivo de geometria.
.painel-deliberacao__rodada-corpo
  contain: layout paint
  will-change: height

.painel-deliberacao__rodada-conteudo
  display: flex
  flex-direction: column
  padding-bottom: 8px

// O avatar abre o cabeçalho do turno; tudo abaixo dele se alinha sob o NOME, não
// sob o avatar. Daí o recuo ser a soma do avatar com o gap do cabeçalho
// (`az-gap-small`) — derivado, não escolhido.
$tamanho-avatar: 28px
$gap-cabecalho: 8px
$recuo-do-conteudo: $tamanho-avatar + $gap-cabecalho

.painel-deliberacao__turno
  display: flex
  flex-direction: column

  // O link entra na lista: sem ele o "ver análise completa" ficava colado na
  // borda do card enquanto todo o resto do turno estava recuado, e lia como se
  // pertencesse ao painel, não ao turno.
  //
  // ★ `.painel-turno-detalhes` (o estruturado INLINE, de quem não tem fala)
  // entrou nesta lista em 19/09/2026: ele é filho direto daqui e ficava colado
  // na margem do cartão enquanto o nome da área acima dele estava recuado — a
  // condição da rodada 3 lia como legenda do painel, não como fala de quem a
  // disse. Não precisa de `::v-deep`: o root de um componente filho carrega
  // TAMBÉM o escopo do pai.
  > .painel-deliberacao__fala,
  > .painel-deliberacao__quote,
  > .painel-deliberacao__linha,
  > .painel-deliberacao__analise,
  > .painel-deliberacao__ver-analise,
  > .painel-turno-detalhes
    margin-left: $recuo-do-conteudo

.painel-avatar
  display: inline-flex
  align-items: center
  justify-content: center
  width: $tamanho-avatar
  height: $tamanho-avatar
  border-radius: 999px

.painel-avatar--pronto
  background: var(--neutral-100)

// ★ A TAG discreta que substituiu o badge preenchido de `conduz`/`cede`
// [owner, 19/09/2026]. Contorno de hairline, sem ponto e sem preenchimento; a
// cor — quando existe — fica no TEXTO, porque a posição da réplica é o desfecho
// do conflito e continua significando. Os tons vêm de `CLASSE_POR_COR`.
.painel-tag
  color: var(--neutral-700)
  display: inline-flex
  align-items: center
  flex-shrink: 0
  padding: 0 6px
  border: 1px solid var(--border-hairline)
  border-radius: var(--radius-sm)

.painel-tag--sucesso
  color: var(--success-text)

.painel-tag--info
  color: var(--primary)

.painel-tag--atencao
  color: var(--warning-text)

.painel-tag--risco
  color: var(--danger-text)

.painel-deliberacao__ver-analise
  display: inline-flex
  align-items: center
  gap: 4px
  padding: 0
  border: 0
  background: none
  // O turno é uma coluna flex: sem isto o `inline-flex` estica para a largura
  // toda e o link vira uma faixa de 700px com a seta lá na ponta.
  align-self: flex-start

  // `.ia-block-seta` nasce com `margin-left: auto` para encostar no fim da faixa
  // do cabeçalho. Aqui a seta é do rótulo — anda colada nele.
  .ia-block-seta
    margin-left: 0

// O trecho citado é fala de OUTRA área dentro deste turno: recuo e borda mais
// fina o separam do que quem replica está dizendo.
.painel-deliberacao__quote
  display: flex
  flex-direction: column
  border-left: 2px solid var(--gray-3)
  padding-left: 8px
  margin-left: 4px

// Mesmo motivo do corpo: a análise completa também anima `height`.
.painel-deliberacao__analise
  contain: layout paint
  will-change: height
  display: flex
  flex-direction: column

// A fala é o corpo do turno: linha mais alta que a dos chips, porque é prosa
// para ler, não rótulo para escanear.
.painel-deliberacao__fala
  line-height: 1.5
  white-space: pre-wrap

// O `v-tooltip`/`v-menu` do BaseStatus renderiza um wrapper 0×0 que ainda conta
// como item de flex — dentro de um container com `gap`, o espaço ao redor dele
// dobra. `display: contents` o tira da árvore de layout sem afetar o chip.
::v-deep .painel-deliberacao__cabecalho .v-tooltip
  display: contents
</style>
